import type { GraphQLResult } from './gql';
import type { LiteralToPrimitive, Primitive } from './typescript';

export const sameArrays = (array1: any[], array2: any[]) =>
  array1.length === array2.length &&
  [...array1]
    .sort()
    .every((value, index) => value === [...array2].sort()[index]);

export function groupBy<T extends GraphQLResult>(
  list: T[],
  key: (obj: T) => T['__typename']
): Partial<{ [key in T['__typename']]: Extract<T, { __typename: key }>[] }>;

export function groupBy<T, K extends PropertyKey>(
  list: T[],
  key: (obj: T) => K
): Partial<Record<K, T[]>>;

export function groupBy<T, K extends PropertyKey>(
  list: T[],
  key: (obj: T) => K
) {
  return list.reduce<Partial<Record<K, T[]>>>((res, obj) => {
    const v = key(obj);
    (res[v] = res[v] || []).push(obj);
    return res;
  }, {});
}

export function arrayToObject<T, S = T>({
  key,
  value,
  list,
}: {
  key: (obj: T) => string;
  value: (obj: T) => S;
  list: T[];
}) {
  return list.reduce(
    (acc, curr) =>
      key(curr)
        ? {
            ...acc,
            [key(curr)]: value(curr),
          }
        : acc,
    {}
  );
}

export function sortBy<T>(key: (obj: T) => Date | string | number, list: T[]) {
  return list.sort((a, b) => {
    if (key(a) < key(b)) {
      return -1;
    }
    if (key(a) > key(b)) {
      return 1;
    }
    return 0;
  });
}

export function uniqueBy<T>(key: (obj: T) => string, list: T[]) {
  const keys = list.map(key);
  return list.filter((item, pos) => keys.indexOf(key(item)) === pos);
}

// So that Object.keys returns the correct type: cf https://github.com/microsoft/TypeScript/pull/12253
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const keys = Object.keys as <T>(o: T) => Extract<keyof T, string>[];

function randomEl(array: []): undefined;
function randomEl<T>(array: Readonly<T[]>): T;
function randomEl<T>(array: Readonly<T[]>): T | undefined {
  if (!array.length) return undefined;
  return array[Math.floor(Math.random() * array.length)];
}
/** Need this solo export because vautour is struggling for some reason */
export const randomElement = randomEl;

// Use Fisher-Yates shuffle algorithm
export function shuffle<T>(array: T[]): T[] {
  let currentIndex = array.length;
  const shuffled = [...array];

  // While there remain elements to shuffle...
  while (currentIndex !== 0) {
    // Pick a remaining element...
    const randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;

    // And swap it with the current element.
    [shuffled[currentIndex], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[currentIndex],
    ];
  }
  return shuffled;
}

export enum LenientPolicy {
  UNKNOWN_AT_TAIL = 1,
  UNKNOWN_AT_HEAD = -1,
}

export function sortByArrayIndex<T>(array: readonly T[], a: T, b: T): number {
  return array.indexOf(a) - array.indexOf(b);
}

export const indexBy = <T>(
  array: T[],
  key: (obj: T) => string
): Record<string, T> => arrayToObject({ key, value: e => e, list: array });

export function range(size: number) {
  return Array.from(Array(size).keys());
}

export function findLastIndex<T>(
  array: Array<T>,
  predicate: (value: T, index: number, obj: T[]) => boolean
): number {
  let l = array.length;
  while (l) {
    l -= 1;
    if (predicate(array[l], l, array)) return l;
  }
  return -1;
}

type StrictLookup<T, K extends keyof T> = <P extends T[K]>(
  v: P
) => T & Readonly<Record<K, P>>;

type Fallback<T, K extends keyof T> = <P extends LiteralToPrimitive<T[K]>>(
  v: P
) => (T & Readonly<Record<K, P>>) | undefined;

type StrictFinder<T, K extends keyof T> = StrictLookup<T, K> & Fallback<T, K>;

type _FindByKey<E, K extends keyof E> = E[K] extends Primitive
  ? `findBy${Capitalize<K & string>}`
  : never;

type FindBy<T extends readonly unknown[]> = {
  [K in keyof T[number] as _FindByKey<T[number], K>]: Readonly<T> extends T
    ? StrictFinder<T[number], K> // Handle makeSearchable([{…}] as const);
    : (v: LiteralToPrimitive<T[number][K]>) => T[number] | undefined; // Handle non const cases
};

export const makeSearchable = <T extends readonly Record<string, unknown>[]>(
  data: T
) => {
  return new Proxy(data, {
    get(array: T, prop: string, receiver: any) {
      const prefix = 'findBy';
      if (typeof prop === 'string' && prop.startsWith(prefix)) {
        let key = prop.replace(prefix, '');
        key = key[0].toLowerCase() + key.slice(1);
        return (value: string) => array.find(x => x[key] === value);
      }
      return Reflect.get(array, prop, receiver);
    },
  }) as T & FindBy<T>;
};
